import { Head, Link, usePage } from "@inertiajs/react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PageContainer from "@/components/marketing/PageContainer";
import SectionHeading from "@/components/marketing/SectionHeading";
import { GuideContents, GuideDownload, GuideItems, GuideLinks } from "@/components/marketing/GuideContent";

export default function ContentPageShow({ page }) {
  const { routes } = usePage().props;
  const ctaHref = page.cta?.href || routes.new_school_application;
  const ctaLabel = page.cta?.label || "Sign up your school";
  const sections = page.sections.map((section, index) => (
    <ContentSection key={section.heading} section={section} id={`section-${index + 1}`} />
  ));

  return (
    <MarketingLayout>
      <Head title={page.title} />

      <PageContainer width="default" className="pt-10 md:pt-14 pb-8">
        <div className="space-y-6 max-w-3xl">
          {page.eyebrow ? <Badge className="uppercase tracking-wider">{page.eyebrow}</Badge> : null}
          <div className="space-y-4">
            <h1 className="text-4xl md:text-6xl leading-tight text-foreground">{page.h1}</h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">{page.dek}</p>
          </div>
          {!page.download ? (
            <div className="flex flex-wrap gap-3">
              <Button size="lg" asChild>
                <Link href={ctaHref}>
                  {ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href={routes.readathon_software}>See the software</Link>
              </Button>
            </div>
          ) : null}
        </div>
      </PageContainer>

      {page.download ? <GuideDownload download={page.download} cta={page.cta} /> : null}
      {page.sections.length >= 6 ? <GuideContents sections={page.sections} /> : null}
      {sections}

      {page.faqs?.length ? (
        <PageContainer width="wide" className="pt-10">
          <SectionHeading flourish="common questions" title="FAQ" />
          <div className="grid gap-4 md:grid-cols-2 mt-10">
            {page.faqs.map((faq) => (
              <Card key={faq.question} className="bg-card/70">
                <CardContent className="p-6 md:p-6 space-y-2">
                  <h2 className="text-lg font-semibold text-foreground">{faq.question}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </PageContainer>
      ) : null}

      {page.related?.length ? (
        <PageContainer width="default" className="pt-10">
          <SectionHeading align="left" flourish="keep reading" title="Related pages" />
          <div className="grid gap-3 mt-8 md:grid-cols-2">
            {page.related.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="flex items-center justify-between gap-4 rounded-md border border-border/70 bg-card/50 p-4 text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary"
              >
                <span>{link.label}</span>
                <ArrowRight className="h-4 w-4 shrink-0" />
              </Link>
            ))}
          </div>
        </PageContainer>
      ) : null}

      <PageContainer width="narrow" className="text-center space-y-6 pb-12 md:pb-16">
        <SectionHeading
          flourish="early adopter discount"
          title={page.cta?.heading || "Bring your next readathon online."}
          subhead={page.cta?.body || "PagePledge is onboarding schools that want simpler readathon donations, reading logs, and campaign tracking."}
        />
        <Button size="lg" asChild>
          <Link href={ctaHref}>
            {ctaLabel}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </PageContainer>
    </MarketingLayout>
  );
}

function ContentSection({ section, id }) {
  return (
    <PageContainer id={id} width={section.cards?.length ? "wide" : "default"} className="pt-10">
      <SectionHeading
        align="left"
        flourish={section.eyebrow}
        title={section.heading}
        subhead={section.subhead}
      />

      {section.body?.length ? (
        <div className="mt-8 space-y-4 text-muted-foreground leading-relaxed max-w-3xl">
          {section.body.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      ) : null}

      {section.bullets?.length ? (
        <div className="grid gap-3 mt-8 max-w-3xl">
          {section.bullets.map((item) => (
            <div key={item} className="flex gap-3 text-sm md:text-base text-muted-foreground">
              <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      ) : null}

      {section.items?.length ? <GuideItems items={section.items} /> : null}
      {section.links?.length ? <GuideLinks links={section.links} /> : null}

      {section.cards?.length ? (
        <div className="grid gap-5 md:grid-cols-3 mt-10">
          {section.cards.map((card) => (
            <Card key={card.title} className="bg-card/70">
              <CardHeader>
                <CardTitle className="text-xl">{card.title}</CardTitle>
                <CardDescription className="text-sm leading-relaxed">{card.body}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : null}
    </PageContainer>
  );
}
